import type { Puzzle } from './scoring'
import { todayUTC } from './history'

const CACHE_KEY = 'wordevery.puzzles.v1'
const CACHE_SIZE = 14
const DATE = /^\d{4}-\d{2}-\d{2}$/

/** The archive date requested in the URL (?date=YYYY-MM-DD), or null for today. */
export function requestedDate(search: string = window.location.search, today: string = todayUTC()): string | null {
  const date = new URLSearchParams(search).get('date')
  return date && DATE.test(date) && date < today ? date : null
}

function readCache(): Record<string, Puzzle> {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') as Record<string, Puzzle>
  } catch {
    return {}
  }
}

function writeCache(puzzle: Puzzle): void {
  try {
    const cache = { ...readCache(), [puzzle.date]: puzzle }
    const keep = Object.keys(cache).sort().slice(-CACHE_SIZE)
    localStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries(keep.map((d) => [d, cache[d]]))))
  } catch {
    // Caching is best-effort.
  }
}

/** Loads today's puzzle, or an archive day's; falls back to the local copy offline. */
export async function fetchPuzzle(date: string | null): Promise<Puzzle> {
  const day = date ?? todayUTC()
  try {
    const res = await fetch(date ? `/api/puzzle/${date}` : '/api/puzzle/today')
    if (res.status === 404) {
      throw new Error(date ? 'There is no puzzle for that day.' : "Today's puzzle isn't out yet. Check back soon!")
    }
    if (!res.ok) throw new Error('Could not load the puzzle.')
    const puzzle = (await res.json()) as Puzzle
    writeCache(puzzle)
    return puzzle
  } catch (err) {
    const cached = readCache()[day]
    if (cached) return cached
    throw err instanceof TypeError ? new Error("You're offline and this puzzle isn't saved yet.") : err
  }
}

export async function fetchPuzzleDates(): Promise<string[]> {
  const res = await fetch('/api/puzzles')
  if (!res.ok) throw new Error('Could not load past puzzles.')
  return ((await res.json()) as { dates: string[] }).dates
}

export function formatDate(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { ...options, timeZone: 'UTC' })
}
